import { TopNav } from "@dashboard/components/AppLayout/TopNav";
import { type ConfirmButtonTransitionState } from "@dashboard/components/ConfirmButton/ConfirmButton";
import { DetailPageLayout } from "@dashboard/components/Layouts/Detail";
import { Savebar } from "@dashboard/components/Savebar";
import { type AtticErrorFragment } from "@dashboard/graphql";
import useNavigator from "@dashboard/hooks/useNavigator";
import { type PermissionData } from "@dashboard/permissionGroups/components/PermissionGroupDetailsPage/PermissionGroupDetailsPage";
import { useRef, useState } from "react";
import { useIntl } from "react-intl";

import { formMessages, sectionMessages } from "../../messages";
import { AtticApiKeysDestinationIcon } from "../../topNav";
import { type AtticApiKeyFormData, type AtticApiKeySubmit } from "../../types";
import { atticApiKeyListPath } from "../../urls";
import { isExpiryInputUnreadable } from "../../utils";
import AtticApiKeyGeneralCard from "../AtticApiKeyGeneralCard/AtticApiKeyGeneralCard";
import AtticApiKeyPermissionsCard from "../AtticApiKeyPermissionsCard/AtticApiKeyPermissionsCard";

export interface AtticApiKeyCreatePageProps {
  permissionChoices: PermissionData[];
  errors: AtticErrorFragment[];
  disabled: boolean;
  saveButtonBarState: ConfirmButtonTransitionState;
  minDay: string;
  onSubmit: AtticApiKeySubmit;
}

export const AtticApiKeyCreatePage = ({
  permissionChoices,
  errors,
  disabled,
  saveButtonBarState,
  minDay,
  onSubmit,
}: AtticApiKeyCreatePageProps) => {
  const intl = useIntl();
  const navigate = useNavigator();
  const [data, setData] = useState<AtticApiKeyFormData>({
    label: "",
    expiryDay: "",
    readOnly: false,
    permissions: [],
  });
  const expiryRef = useRef<HTMLDivElement>(null);
  const change = (patch: Partial<AtticApiKeyFormData>) =>
    setData(current => ({ ...current, ...patch }));

  return (
    <DetailPageLayout>
      <TopNav
        href={atticApiKeyListPath}
        hrefIcon={<AtticApiKeysDestinationIcon />}
        hrefTitle={intl.formatMessage(sectionMessages.allKeys)}
        title={intl.formatMessage(formMessages.createTitle)}
      />
      <DetailPageLayout.Content>
        <AtticApiKeyGeneralCard
          data={data}
          errors={errors}
          disabled={disabled}
          minDay={minDay}
          expiryRef={expiryRef}
          onChange={change}
        />
      </DetailPageLayout.Content>
      <DetailPageLayout.RightSidebar>
        <AtticApiKeyPermissionsCard
          choices={permissionChoices}
          selected={data.permissions}
          errors={errors}
          disabled={disabled}
          onChange={permissions => change({ permissions })}
        />
      </DetailPageLayout.RightSidebar>
      <Savebar>
        <Savebar.Spacer />
        <Savebar.CancelButton onClick={() => navigate(atticApiKeyListPath)} />
        <Savebar.ConfirmButton
          transitionState={saveButtonBarState}
          onClick={() => onSubmit(data, isExpiryInputUnreadable(expiryRef.current))}
          disabled={disabled || data.label.trim() === ""}
          data-test-id="attic-api-key-save"
        />
      </Savebar>
    </DetailPageLayout>
  );
};

export default AtticApiKeyCreatePage;
